import { prettyPrintDateRangeDate } from "@prismicio/editor-support/Date"
import { keys } from "@prismicio/editor-support/Object"
import {
  Badge,
  Box,
  Card,
  CardContent,
  CardHeader,
  CardHeaderTitle,
  Separator,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"
import { Fragment, type ReactNode } from "react"

import { isNonEnterprisePlan } from "../billing/billingUtils"
import { useSubscriptionQuery } from "../billing/useSubscription"
import {
  CurrentPlanCardItem,
  type CurrentPlanCardItemProps,
  CurrentPlanCardItemSkeleton,
} from "./CurrentPlanCardItem"
import { usageSummaryItemTypes } from "./usageSummaryItemTypes"

export function CurrentPlanCard() {
  const { data: subscription } = useSubscriptionQuery()
  return (
    <CurrentPlanCardTemplate
      badge={<Badge color="purple" title={subscription.nickname} />}
      resetDate={
        isNonEnterprisePlan(subscription) ? (
          <Text color="grey11" variant="small">
            Your plan resets on{" "}
            {prettyPrintDateRangeDate(subscription.usagePeriodEnd, {
              year: false,
            })}
          </Text>
        ) : undefined
      }
      renderCurrentPlanCardItem={CurrentPlanCardItem}
    />
  )
}

interface CurrentPlanCardTemplateProps {
  badge: ReactNode
  resetDate: ReactNode
  renderCurrentPlanCardItem: (props: CurrentPlanCardItemProps) => ReactNode
}

function CurrentPlanCardTemplate(props: CurrentPlanCardTemplateProps) {
  const { badge, resetDate, renderCurrentPlanCardItem } = props
  return (
    <Card
      color="purple2"
      gap={4}
      paddingBlock={16}
      paddingInline={24}
      variant="outlined"
    >
      <CardHeader>
        <CardHeaderTitle variant="bold">Current plan</CardHeaderTitle>
        {badge}
      </CardHeader>
      <CardContent gap={16}>
        {resetDate ?? <Box margin={{ top: "-4" }} />}
        {keys(usageSummaryItemTypes).map((itemTypeKey) => (
          <Fragment key={itemTypeKey}>
            <Separator color="purple" style="dashed" />
            {renderCurrentPlanCardItem({ itemTypeKey })}
          </Fragment>
        ))}
      </CardContent>
    </Card>
  )
}

export function CurrentPlanCardSkeleton() {
  return (
    <CurrentPlanCardTemplate
      badge={<Skeleton width={65} height={28} color="purple5" />}
      resetDate={<Skeleton width={152} height={16} />}
      renderCurrentPlanCardItem={CurrentPlanCardItemSkeleton}
    />
  )
}
